"use client";

import { useCallback, useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import type { PlatformAnnouncement } from "@prisma/client";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { cf } from "@/components/brand/sfturnos-styles";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useConfirm } from "@/hooks/useConfirm";

export function AnnouncementsEditor() {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const [rows, setRows] = useState<PlatformAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const r = await fetchWithSessionSync("/api/admin/announcements", { credentials: "include" }, refresh);
    if (r.ok) setRows(await r.json());
    setLoading(false);
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(id: string) {
    if (!(await confirm("¿Eliminar este aviso?"))) return;
    await fetchWithSessionSync(`/api/admin/announcements/${id}`, { method: "DELETE", credentials: "include" }, refresh);
    void load();
  }

  if (loading) return <LoadingFrame variant="light" compact minHeight={120} />;

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "8" })}>
      <NewAnnouncementForm onCreated={() => void load()} />

      {rows.map((row) => (
        <div key={row.id} className={cx(cf.card, cf.cardPadding)}>
          <div className={css({ display: "flex", justifyContent: "space-between", gap: "4", flexWrap: "wrap", mb: "4" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>
              {row.isActive ? "Activo" : "Pausado"} · creado {new Date(row.createdAt).toLocaleDateString("es-AR")}
            </span>
            <Button type="button" variant="danger" onClick={() => void remove(row.id)}>
              Eliminar
            </Button>
          </div>
          <AnnouncementEditForm row={row} onSaved={() => void load()} />
        </div>
      ))}
    </div>
  );
}

function NewAnnouncementForm({ onCreated }: { onCreated: () => void }) {
  const { refresh } = useAuth();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    await fetchWithSessionSync(
      "/api/admin/announcements",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, body, isActive: true, showOnDashboard: true, showOnLanding: false }),
      },
      refresh,
    );
    setBusy(false);
    setTitle("");
    setBody("");
    onCreated();
  }

  return (
    <form onSubmit={(e) => void submit(e)} className={cx(cf.card, cf.cardPadding, css({ display: "flex", flexDir: "column", gap: "3" }))}>
      <p className={css({ fontWeight: "semibold", color: "#0f172a" })}>Nuevo aviso</p>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Título</span>
        <input className={cf.input} value={title} onChange={(e) => setTitle(e.target.value)} required />
      </label>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Mensaje</span>
        <textarea
          className={cx(cf.input, css({ minH: "24", py: "3", resize: "vertical" }))}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          rows={4}
        />
      </label>
      <Button type="submit" variant="primary" disabled={busy}>
        Publicar aviso
      </Button>
    </form>
  );
}

function AnnouncementEditForm({ row, onSaved }: { row: PlatformAnnouncement; onSaved: () => void }) {
  const { refresh } = useAuth();
  const [title, setTitle] = useState(row.title);
  const [body, setBody] = useState(row.body);
  const [isActive, setIsActive] = useState(row.isActive);
  const [showOnDashboard, setShowOnDashboard] = useState(row.showOnDashboard);
  const [showOnLanding, setShowOnLanding] = useState(row.showOnLanding);
  const [sortOrder, setSortOrder] = useState(String(row.sortOrder));
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    await fetchWithSessionSync(
      `/api/admin/announcements/${row.id}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          body,
          isActive,
          showOnDashboard,
          showOnLanding,
          sortOrder: Number.parseInt(sortOrder, 10) || 0,
        }),
      },
      refresh,
    );
    setBusy(false);
    onSaved();
  }

  return (
    <form onSubmit={(e) => void save(e)} className={css({ display: "flex", flexDir: "column", gap: "4" })}>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Título</span>
        <input className={cf.input} value={title} onChange={(e) => setTitle(e.target.value)} required />
      </label>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Mensaje</span>
        <textarea
          className={cx(cf.input, css({ minH: "28", py: "3", resize: "vertical" }))}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          rows={5}
        />
      </label>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Orden</span>
        <input className={cf.input} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} inputMode="numeric" />
      </label>
      <label className={css({ display: "flex", alignItems: "center", gap: "2", cursor: "pointer" })}>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        <span className={css({ fontSize: "sm" })}>Activo</span>
      </label>
      <label className={css({ display: "flex", alignItems: "center", gap: "2", cursor: "pointer" })}>
        <input type="checkbox" checked={showOnDashboard} onChange={(e) => setShowOnDashboard(e.target.checked)} />
        <span className={css({ fontSize: "sm" })}>Mostrar en panel de profesionales</span>
      </label>
      <label className={css({ display: "flex", alignItems: "center", gap: "2", cursor: "pointer" })}>
        <input type="checkbox" checked={showOnLanding} onChange={(e) => setShowOnLanding(e.target.checked)} />
        <span className={css({ fontSize: "sm" })}>Mostrar en la web pública</span>
      </label>
      <Button type="submit" variant="primary" disabled={busy}>
        Guardar
      </Button>
    </form>
  );
}
